// no default React import needed with react-jsx
import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'
import { IconButton, Tooltip } from '@mui/material'
import { User } from '../types/api'
import { adminApi } from '../lib/sdkClient'

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [totalRows, setTotalRows] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [searchTerm, setSearchTerm] = useState('')
  const [suspendDialog, setSuspendDialog] = useState<{ open: boolean; user: User | null }>({
    open: false,
    user: null,
  })
  const [suspendReason, setSuspendReason] = useState('')

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const { data } = await adminApi.adminUsersArtrackAdminUsersGet(searchTerm || undefined, pageSize, page * pageSize)
      setUsers((data as any)?.items || [])
      setTotalRows((data as any)?.total || 0)
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [page, pageSize, searchTerm])

  const handleSuspendUser = async () => {
    if (!suspendDialog.user || !suspendReason.trim()) return

    try {
      // TODO: implement suspend endpoint in API and SDK if required
      // await adminApi.adminSuspendUser...()
      setSuspendDialog({ open: false, user: null })
      setSuspendReason('')
      fetchUsers()
    } catch (error) {
      console.error('Error suspending user:', error)
    }
  }

  const handleReactivateUser = async (id: number) => {
    void id
    try {
      // TODO: implement reactivate endpoint in API and SDK if required
      // await adminApi.adminReactivateUser...()
      fetchUsers()
    } catch (error) {
      console.error('Error reactivating user:', error)
    }
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'email', headerName: 'Email', minWidth: 240, flex: 1.2 },
    { field: 'display_name', headerName: 'Display Name', minWidth: 220, flex: 1 },
    { field: 'trust_level', headerName: 'Trust Level', minWidth: 160, flex: 0.7 },
    {
      field: 'created_at',
      headerName: 'Registered',
      minWidth: 160,
      flex: 0.7,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: 'last_active_at',
      headerName: 'Last Active',
      minWidth: 160,
      flex: 0.7,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
    },
    { field: 'track_count', headerName: 'Tracks', minWidth: 120, flex: 0.5 },
    { field: 'waypoint_count', headerName: 'Waypoints', minWidth: 140, flex: 0.5 },
    {
      field: 'storage_used_bytes',
      headerName: 'Storage Used',
      minWidth: 140,
      flex: 0.7,
      valueFormatter: (params) => formatBytes(params.value),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 140,
      flex: 0.6,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'active' ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="View Profile">
            <IconButton size="small" onClick={() => { /* Navigate to user profile */ }}>
              <PersonIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === 'active' ? (
            <Tooltip title="Suspend">
              <IconButton size="small" color="warning" onClick={() => setSuspendDialog({ open: true, user: params.row })}>
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Reactivate">
              <IconButton size="small" color="success" onClick={() => handleReactivateUser(params.row.id)}>
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ]

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <Typography variant="h4" component="h1" gutterBottom>
        User Management
      </Typography>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 2, width: '100%' }}>
        <TextField
          placeholder="Search users by email or name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{ minWidth: 300 }}
          fullWidth
        />
      </Paper>

      {/* Data Grid */}
      <Paper sx={{ height: 'calc(100vh - 220px)', width: '100%', maxWidth: '100%' }}>
        <DataGrid
          rows={users}
          columns={columns}
          pageSize={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          page={page}
          onPageChange={(newPage) => setPage(newPage)}
          rowCount={totalRows}
          loading={loading}
          paginationMode="server"
          disableSelectionOnClick
          sx={{ '& .MuiDataGrid-main': { width: '100%' } }}
        />
      </Paper>

      {/* Suspend User Dialog */}
      <Dialog
        open={suspendDialog.open}
        onClose={() => setSuspendDialog({ open: false, user: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Suspend User</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to suspend {suspendDialog.user?.display_name}?
          </Typography>
          <TextField
            label="Reason for suspension"
            multiline
            rows={3}
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendDialog({ open: false, user: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleSuspendUser}
            color="error"
            variant="contained"
            disabled={!suspendReason.trim()}
          >
            Suspend User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}